/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Fragment, memo } from 'react';

import { isAuthenticatedUser } from 'js/lib/user';

import { Grid, breakpoints } from '@coursera/cds-core';
import type { Recommender } from '@coursera/event-pulse-types';

import { getShouldShowUnregisteredOnboarding } from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import InlineLearningGoalQuestion from 'bundles/onboarding-common/components/InlineLearningGoalQuestion';
import ModularOnboardingModuleRenderer from 'bundles/onboarding-common/module-renderers/ModularOnboardingModuleRenderer';
import SearchPageMerchandisingCollection from 'bundles/search-common/components/experiments/merchInSearch/SearchPageMerchandisingCollection';
import { getGoogleAIRefreshProducts } from 'bundles/search-common/components/experiments/merchInSearch/merchandisingInSearchUtils';
import SearchCards from 'bundles/search-common/components/search-cards/SearchCards';
import {
  SEARCH_RESULTS_BEFORE_INLINE_ONBOARDING,
  SEARCH_RESULTS_BEFORE_LOGGED_IN_INLINE_ONBOARDING,
} from 'bundles/search-common/constants';
import type { SearchHit, SearchUpsellHit } from 'bundles/search-common/providers/searchTypes';
import {
  getShouldShowGoogleAISpotlightCollectionsInSearch,
  getShouldShowGoogleAISpotlightCollectionsInSearchWithImpression,
} from 'bundles/search-common/utils/experimentUtils';
import { isFreeQuery } from 'bundles/search-common/utils/pageConfigUtils';
import { isSearchProductHit } from 'bundles/search-common/utils/utils';

const styles = {
  inlineLearningGoalQuestionWrapper: css`
    padding: var(--cds-spacing-300) 6px var(--cds-spacing-600);
    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-50) var(--cds-spacing-300);
    }
  `,
  onboardingModuleContainer: css`
    div {
      margin-top: 0;
    }
  `,
};

type SearchPageBodySectionsProps = {
  hits: Array<SearchHit | SearchUpsellHit>;
  resultsPerPage: number;
  forceOpenInNewTab: boolean;
  eventingData?: {
    searchIndex: string;
    searchRecommender?: Recommender;
    searchIndexPosition: number;
    page?: string;
    searchQuery?: string;
    filtersApplied?: Record<string, string[]>;
  };
  isLoading?: boolean;
  isSearchPage: boolean;
  onScrolledToBottom?: () => void;
  totalHitsDisplayed?: number;
  cardIndexOffset?: number;
  isFirstSection?: boolean;
  page?: number;
  isSeoEntityPagePath?: boolean;
};

// Shared props for SearchCards to reduce duplication
type SearchCardsBaseProps = {
  resultsPerPage: number;
  forceOpenInNewTab: boolean;
  eventingData?: {
    searchIndex: string;
    searchRecommender?: Recommender;
    searchIndexPosition: number;
    page?: string;
    searchQuery?: string;
    filtersApplied?: Record<string, string[]>;
  };
  isLoading?: boolean;
  isSearchPage: boolean;
  onScrolledToBottom?: () => void;
  totalHitsDisplayed?: number;
};

type OnboardingConditions = {
  showLoggedOutInlineOnboarding: boolean;
  showLoggedInInlineOnboarding: boolean;
};

// Hook to determine onboarding conditions
const useOnboardingConditions = (
  query: string | undefined,
  page: number | undefined,
  isSeoEntityPagePath: boolean | undefined,
  isFirstSection: boolean | undefined
): OnboardingConditions => {
  const shouldShowOnboardingForPage = (page === 1 && Boolean(isSeoEntityPagePath)) || Boolean(isFirstSection);

  const showLoggedInInlineOnboarding = isAuthenticatedUser() && !isFreeQuery(query) && shouldShowOnboardingForPage;

  const showLoggedOutInlineOnboarding = getShouldShowUnregisteredOnboarding() && shouldShowOnboardingForPage;

  return { showLoggedOutInlineOnboarding, showLoggedInInlineOnboarding };
};

// Component for rendering search cards with onboarding
type SearchSectionProps = SearchCardsBaseProps & {
  section: Array<SearchHit | SearchUpsellHit>;
  cardIndexOffset?: number;
  onboardingConditions: OnboardingConditions;
};

const SearchSection = ({ section, cardIndexOffset, onboardingConditions, ...searchCardsProps }: SearchSectionProps) => {
  const { showLoggedOutInlineOnboarding, showLoggedInInlineOnboarding } = onboardingConditions;

  // Helper to render SearchCards with consistent props
  const renderSearchCards = (
    hits: Array<SearchHit | SearchUpsellHit>,
    indexOffset?: number,
    isEndOfSplitSection?: boolean
  ) => (
    <SearchCards
      hits={hits}
      cardIndexOffset={indexOffset}
      isEndOfSplitSection={isEndOfSplitSection}
      {...searchCardsProps}
    />
  );

  // No onboarding - simple case
  if (!showLoggedOutInlineOnboarding && !showLoggedInInlineOnboarding) {
    return renderSearchCards(section, cardIndexOffset);
  }

  // Determine split point and onboarding component
  const splitPoint = showLoggedOutInlineOnboarding
    ? SEARCH_RESULTS_BEFORE_INLINE_ONBOARDING
    : SEARCH_RESULTS_BEFORE_LOGGED_IN_INLINE_ONBOARDING;

  const firstHalf = section.slice(0, splitPoint);
  const secondHalf = section.slice(splitPoint);

  const onboardingComponent = (
    <div>
      <Grid container css={styles.onboardingModuleContainer}>
        <Grid item xs={12}>
          <ModularOnboardingModuleRenderer
            entrypoint="SEARCH"
            shouldShowSuccessMessageOnCompletion
            noPageGridContainer
          />
        </Grid>
      </Grid>
      {showLoggedOutInlineOnboarding && (
        <div css={styles.inlineLearningGoalQuestionWrapper}>
          <InlineLearningGoalQuestion isCompact />
        </div>
      )}
    </div>
  );

  const secondHalfOffset = cardIndexOffset ? cardIndexOffset + splitPoint : splitPoint;

  return (
    <Fragment>
      {renderSearchCards(firstHalf, cardIndexOffset)}
      {onboardingComponent}
      {renderSearchCards(secondHalf, secondHalfOffset, true)}
    </Fragment>
  );
};

const SearchPageBodySections = ({
  hits,
  resultsPerPage,
  forceOpenInNewTab,
  eventingData,
  isLoading = false,
  isSearchPage,
  onScrolledToBottom,
  totalHitsDisplayed,
  cardIndexOffset,
  isFirstSection,
  page,
  isSeoEntityPagePath,
}: SearchPageBodySectionsProps) => {
  const shouldShowMerchandising = getShouldShowGoogleAISpotlightCollectionsInSearch();
  const query = eventingData?.searchQuery;
  const onboardingConditions = useOnboardingConditions(query, page, isSeoEntityPagePath, isFirstSection);

  if (!Array.isArray(hits)) return null;

  // Handle Google AI impression tracking
  const searchHits = hits.filter((hit) => Array.isArray(hit) && isSearchProductHit(hit));
  const googleAIRefreshProducts = getGoogleAIRefreshProducts(searchHits);
  if (googleAIRefreshProducts && googleAIRefreshProducts.length > 0 && isSearchPage) {
    getShouldShowGoogleAISpotlightCollectionsInSearchWithImpression();
  }

  // Split hits into sections
  const sections: Array<Array<SearchHit | SearchUpsellHit>> = [];
  for (let i = 0; i < hits.length; i += resultsPerPage) {
    sections.push(hits.slice(i, i + resultsPerPage));
  }
  if (sections.length === 0 && isLoading) sections.push([]);

  const searchCardsBaseProps: SearchCardsBaseProps = {
    resultsPerPage,
    forceOpenInNewTab,
    eventingData,
    isLoading,
    isSearchPage,
    onScrolledToBottom,
    totalHitsDisplayed,
  };

  return (
    <div>
      {sections.map((section, index) => {
        const sectionOffset = cardIndexOffset ? cardIndexOffset + index * resultsPerPage : undefined;

        return (
          <div key={`section-${section[0]?.id}`}>
            <SearchSection
              section={section}
              cardIndexOffset={sectionOffset}
              onboardingConditions={onboardingConditions}
              {...searchCardsBaseProps}
            />
            {shouldShowMerchandising && isSearchPage && (
              <SearchPageMerchandisingCollection
                hits={section}
                isLoading={isLoading}
                eventingData={eventingData}
                forceOpenInNewTab={forceOpenInNewTab}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default memo(SearchPageBodySections);
